/* DISPATCHER */
import { AppDispatcher } from 'dispatcher';

/* STORE */
import { AuthStore } from './AuthStore';
import { BaseStore } from './BaseStore';

/* CONSTANTS */
import * as constants from 'vd-constants';

/* MISC */
import * as SaveClipUtils from 'components/SavePanel/utils/saveClipUtils';
import forEach from 'lodash/forEach';
import get from 'lodash/get';
import has from 'lodash/has';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import isObject from 'lodash/isObject';
import round from 'lodash/round';
import set from 'lodash/set';
import assign from 'object-assign';
import * as DateHandler from '../utils/DateHandler';
import DevicePollHandler from '../utils/DevicePollHandler';
import * as EventUtil from '../utils/EventUtil';

const COMPLETE_STATES = [
  constants.CLOUD_STATUS_COMPLETE,
  constants.CLOUD_STATUS_COMPLETE_WITH_ERRORS,
  constants.CLOUD_STATUS_ERROR,
];

/**
 * @typedef {Object} StatusResult
 * @property {string} state
 * @property {number} pushedSegments
 * @property {number} totalSegments
 */

/**
 *
 * @type {DevicePollHandler}
 * @private
 */
const PollHandler = new DevicePollHandler();

/**
 *
 * @type {Object}
 * @private
 */
const _events = {};

/**
 *
 * @param {Object} event
 * @param {Object} siteCamerasById
 */
const initSharedBookmark = function initSharedBookmark(event, siteCamerasById) {
  const eventId = event.id;
  if (
    EventUtil.isShared(event) &&
    EventUtil.isPushToCloudInProgress(event) &&
    !has(_events, eventId)
  ) {
    _events[eventId] = {
      params: {},
      status: {
        event: {},
        streams: {},
      },
    };

    const start = event.shareStartTime
      ? DateHandler.getTimestamp(event.shareStartTime)
      : DateHandler.getTimestamp(event.startTimeLocal);
    const end = event.shareEndTime
      ? DateHandler.getTimestamp(event.shareEndTime)
      : DateHandler.getTimestamp(event.endTimeLocal);

    forEach(get(event, ['cameras']), (camera) => {
      const deviceId = siteCamerasById[camera.chId]?.deviceId;
      forEach(get(camera, ['streams']), (stream) => {
        // If stream has the "totalSegments" property we know it's a stream that has been pushed to cloud
        if (
          has(stream, 'totalSegments') &&
          get(stream, 'source') !== constants.VIDEO_SOURCE_CLOUD
        ) {
          set(_events, [eventId, 'params', stream.id], {
            deviceId,
            cameraId: camera.cameraId ?? camera.id,
            start,
            end,
          });
        }
      });
    });
  }
};

const changeDurationSharedBookmark = function changeDurationSharedBookmark(event) {
  const eventId = event.id;
  if (has(_events, eventId)) {
    const start = event.shareStartTime
      ? DateHandler.getTimestamp(event.shareStartTime)
      : DateHandler.getTimestamp(event.startTimeLocal);
    const end = event.shareEndTime
      ? DateHandler.getTimestamp(event.shareEndTime)
      : DateHandler.getTimestamp(event.endTimeLocal);

    forEach(get(_events, [eventId, 'params']), (paramSet) => {
      paramSet.start = start;
      paramSet.end = end;
    });

    set(_events, [eventId, 'status', 'event', 'state'], constants.CLOUD_STATUS_IN_PROGRESS);
    set(_events, [eventId, 'status', 'event', 'percent'], 0);
  }
};

/**
 * Get the push-to-cloud status from CloudVMS for each relevant stream in the event.
 * @param {string} eventId
 * @returns {Promise<Record<string, StatusResult>>} Resolves to an array of stream status objects
 */
const getPushStatus = function getPushStatus(eventId) {
  return new Promise((resolve) => {
    const requests = [];
    const params = _events[eventId]?.params || {};

    Object.keys(params).forEach((streamId) => {
      const streamParams = params[streamId];
      const request = new Promise((innerResolve) => {
        SaveClipUtils.getPushToCloudStatus({
          tenantId: AuthStore.getUser().app_metadata.tenantId,
          eventId,
          deviceId: streamParams.deviceId,
          cameraId: streamParams.cameraId,
          streamId,
          start: streamParams.start,
          end: streamParams.end,
        })
          .then((result) => {
            return innerResolve({
              streamId,
              data: result,
            });
          })
          .catch((error) => {
            console.error(`Error getting push-to-cloud status from CloudVMS`, error);
            // Manually set stream to error state
            return innerResolve({
              streamId,
              data: {
                state: constants.CLOUD_STATUS_ERROR,
              },
            });
          });
      });

      requests.push(request);
    });

    Promise.all(requests)
      .then((results) => {
        const parsedResults = {};

        forEach(results, (result) => {
          if (result && has(result, ['data']) && isObject(result.data) && !isEmpty(result.data)) {
            parsedResults[result.streamId] = {
              state: result.data.state,
              pushedSegments: result.data.pushedSegments || 0,
              totalSegments: result.data.totalSegments || 0,
            };
          }
        });

        return resolve(parsedResults);
      })
      .catch((error) => {
        // Shouldn't be able to get here
        console.error(`Error getting push-to-cloud status for streams`, error);
        return resolve();
      });
  });
};

/**
 * Callback function for the poll handler which contains the business logic for each polling cycle. The push-to-cloud
 * status for each relevant stream in the event is processed to determine progress percent per stream and overall status
 * of the event.
 * @param {string} eventId
 * @param {Function} continueCallback
 * @param {Function} stopCallback
 * @param {Function} retryCallback
 */
const processPushStatus = function processPushStatus(
  eventId,
  continueCallback,
  stopCallback,
  retryCallback,
) {
  getPushStatus(eventId)
    .then((parsedResults) => {
      // Stop if no results for event
      if (isEmpty(parsedResults)) {
        stopCallback();
        return;
      }

      const allStates = [];
      let eventTotalSegments = 0;
      let eventPushedSegments = 0;

      forEach(parsedResults, (streamStatus, streamId) => {
        const streamState = streamStatus.state;
        const streamPushedSegments = streamStatus.pushedSegments;
        const streamTotalSegments = streamStatus.totalSegments;
        const streamPercent = calculatePercent(
          streamPushedSegments,
          streamTotalSegments,
          streamState,
        );

        allStates.push(streamState);
        eventPushedSegments += streamPushedSegments;
        eventTotalSegments += streamTotalSegments;

        _events[eventId]['status']['streams'][streamId] = {
          state: streamState,
          pushedSegments: streamPushedSegments,
          totalSegments: streamTotalSegments,
          percent: streamPercent,
        };
      });

      // Determine overall event state (note that a stream can have states "inProgress", "complete" or "error")
      let eventState;
      if (isEmpty(allStates) || includes(allStates, constants.CLOUD_STATUS_IN_PROGRESS)) {
        // If at least one in-progress state or no stream status (e.g. push-to-cloud not yet initiated on CloudVMS)
        eventState = constants.CLOUD_STATUS_IN_PROGRESS;
      } else {
        const hasError = includes(allStates, constants.CLOUD_STATUS_ERROR);
        const hasComplete = includes(allStates, constants.CLOUD_STATUS_COMPLETE);

        if (!hasError && hasComplete) {
          // All streams have complete state
          eventState = constants.CLOUD_STATUS_COMPLETE;
        } else if (hasError && !hasComplete) {
          // All streams have error state
          eventState = constants.CLOUD_STATUS_ERROR;
        } else if (hasError && hasComplete) {
          // Combination of error and complete states
          eventState = constants.CLOUD_STATUS_COMPLETE_WITH_ERRORS;
        } else {
          console.error(`Error determining overall event push-to-cloud state (event ID=${eventId}`);
        }
      }

      const eventPercent = calculatePercent(eventPushedSegments, eventTotalSegments, eventState);
      _events[eventId]['status']['event'] = {
        state: eventState,
        pushedSegments: eventPushedSegments,
        totalSegments: eventTotalSegments,
        percent: eventPercent,
      };

      PollCloudStore.emitChange();

      switch (eventState) {
        case constants.CLOUD_STATUS_IN_PROGRESS: {
          continueCallback();
          break;
        }
        case constants.CLOUD_STATUS_ERROR:
        case constants.CLOUD_STATUS_COMPLETE_WITH_ERRORS: {
          retryCallback(constants.POLLING_DEFAULT_RETRY_INTERVAL_MS);
          break;
        }
        case constants.CLOUD_STATUS_COMPLETE: {
          stopCallback();
          break;
        }
      }
    })
    .catch((error) => {
      // Shouldn't be able to get here
      console.error(`Error processing push-to-cloud status`, error);
    });
};

/**
 *
 * @param {number} pushedSegments
 * @param {number} totalSegments
 * @param {string} state
 * @returns {number|undefined} Returns the rounded percent value or undefined
 */
const calculatePercent = function calculatePercent(pushedSegments, totalSegments, state) {
  let percent;
  if (state === constants.CLOUD_STATUS_IN_PROGRESS) {
    if (pushedSegments > totalSegments && pushedSegments > 0) {
      percent = pushedSegments / (pushedSegments + 1);
    } else if (totalSegments > 0) {
      percent = pushedSegments / totalSegments;
    } else {
      percent = 0;
    }

    percent = round(percent * 100, 0);

    // We don't want percent to be 100 while in-progress
    percent = percent >= 100 ? 99 : percent;
  } else if (
    state === constants.CLOUD_STATUS_COMPLETE ||
    state === constants.CLOUD_STATUS_COMPLETE_WITH_ERRORS
  ) {
    percent = 100;
  }

  return percent;
};

const getEventId = function getEventId(event) {
  return isObject(event) ? event.id : event;
};

const getStreamId = function getStreamId(stream) {
  return isObject(stream) ? stream.id : stream;
};

const PollCloudStore = assign({}, BaseStore, {
  getEvents() {
    return _events;
  },

  /**
   * Get the progress of all streams of the event
   * @param {string} eventId - The event ID
   * @returns {number} percentage of the event
   */
  getEventProgress(eventId) {
    return get(_events, [eventId, 'status', 'event', 'percent']);
  },

  getPollingState(event, stream) {
    const eventId = getEventId(event);
    const streamId = getStreamId(stream);

    // Using get since event might not exist in cached store (or is being initiated)
    return isNil(streamId)
      ? get(_events, [eventId, 'status', 'event', 'state'])
      : get(_events, [eventId, 'status', 'streams', streamId, 'state']);
  },

  getPushStatus(eventId) {
    return getPushStatus(eventId);
  },

  isInProgress(event, stream) {
    const state = PollCloudStore.getPollingState(event, stream);
    return state === constants.CLOUD_STATUS_IN_PROGRESS;
  },

  /**
   *
   * @param {Object} event
   * @param {string} [pollingState]
   * @param {string} [eventState]
   */
  getPrioritizedState(event, pollingState, eventState) {
    const pollingStatus = pollingState ? pollingState : PollCloudStore.getPollingState(event);
    const eventStatus = eventState ? eventState : event.pushToCloudStatus;

    let cloudStatus = pollingStatus || eventStatus;

    // Complete states take priority (polling, then event)
    if (includes(COMPLETE_STATES, pollingStatus)) {
      cloudStatus = pollingStatus;
    } else if (includes(COMPLETE_STATES, eventStatus)) {
      cloudStatus = eventStatus;
    }

    return cloudStatus;
  },
});

PollCloudStore.dispatchToken = AppDispatcher.register((action) => {
  switch (action.type) {
    case constants.CLOUD_POLL_START: {
      const eventId = action.event.id;

      if (action.durationChange && has(_events, [eventId])) {
        changeDurationSharedBookmark(action.event);

        PollCloudStore.emitChange();
      } else {
        initSharedBookmark(action.event, action.siteCamerasById);
      }

      PollHandler.startPollingEvent(
        eventId,
        action.event.cameras,
        action.accessibleAddresses,
        action.siteCamerasById,
        processPushStatus,
        {},
        action.trackerName,
      );

      break;
    }
    case constants.CLOUD_POLL_STOP: {
      PollHandler.stopPollingEvent(
        action.event.id,
        action.event.cameras,
        action.siteCamerasById,
        action.trackerName,
      );
      break;
    }
    case constants.CLOSE_EVENT_PLAYER:
    case constants.VISIT_PAGE: {
      PollHandler.stopPollingAllEvents();
      break;
    }
  }
});

export default PollCloudStore;
